import { FC, useEffect, useRef, useState } from 'react';
import { Button, Modal, Row, Col, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { GET_SIGNED_URL } from '../../Pages/candidate/gql';
import { UPDATE_APPLICANT_UPDATE } from '../../Pages/job-order/gql';
import { VIEW_CANDIDATE_ROUTE } from '../../utils/constant';
import { formatDateUS, downloadSignedUrlFile } from '../../utils/commonFn';
import { countryWiseDetailedlocation } from '../../utils/helper';
import NoDataFound from '../layout/NoDataFound';
import NotesTab from './NotesTab';
import ApplyTab from './ApplyTab';
import GradeWidget from './GradeWidget';
import LINKEDIN_ICON from '../../assets/images/linkedin.png';
import './style.scss';

const ADVANCED_STATUSES = ['SUBMITTED', 'INTERVIEWED', 'OFFERED', 'PLACED'];
const DECLINE_HIDDEN_STATUSES = ['SUBMITTED', 'INTERVIEWED', 'OFFERED', 'PLACED', 'CANDIDATE_DECLINE', 'CLIENT_DECLINE'];

// Statuses that should drop the recruiter into the Notes tab with a pre-filled
// category. The "Phone Call" category also triggers backend ownership grant.
const STATUS_TO_CATEGORY: Record<string, string> = {
  CALLED: 'Phone Call',
  EMAILED: 'Email Sent',
};

type WorkspaceTab = 'detail' | 'notes' | 'apply';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  applicant: any;
  jobOrderId?: string;
  onDeclined?: (applicantId: string) => void;
  isEdit?: boolean;
  setStatusOptions?: Array<{ label: string; value: string }>;
  onStatusChange?: (applicantId: string, status: string) => void;
  /** Mark the applicant as processed without closing the modal (Called/Emailed). */
  onProcessed?: (applicantId: string) => void;
  /** Recsys candidate-search read-only mode: render DISABLED "Add to Shortlist"
   *  + "Decline" placeholders and suppress the real JobApplicant decline/status
   *  actions. Only used as a read-only fallback when onShortlist/onDecline
   *  aren't provided; only the candidate-recommendations page passes this, so
   *  /new-applicants is unaffected. */
  showDisabledDispositions?: boolean;
  /** Forwarded to the Apply tab — fired after a successful apply so the host
   *  page can react (e.g. Candidate Search drops the candidate from results). */
  onApplied?: (candidateId: string, jobOrderId: string) => void;
  /** Story 2b recsys dispositions (RecsysDispositions table, NOT a JobApplicant
   *  status). When provided, "Add to Shortlist" / "Decline" render ENABLED and
   *  call these instead of the disabled placeholders; suppresses the real
   *  JobApplicant Set-Status/Decline actions below (same read-only-mode gating
   *  as showDisabledDispositions). Only the candidate-recommendations page
   *  passes these, so /new-applicants is unaffected. */
  onShortlist?: (candidateId: string) => void | Promise<void>;
  onDecline?: (candidateId: string) => void | Promise<void>;
  /** Restore a declined candidate (deletes the DECLINED disposition). Shown as
   *  the ONLY action when the candidate is currently declined. */
  onRestore?: (candidateId: string) => void | Promise<void>;
  /** The candidate's current recsys disposition for the selected job, if any.
   *  Drives which disposition buttons show: SHORTLISTED hides "Add to
   *  Shortlist" (already shortlisted); DECLINED shows only "Restore". null/
   *  undefined (opened from Batch/Search) shows both Add + Decline. */
  dispositionStatus?: 'SHORTLISTED' | 'DECLINED' | null;
  /** Forwarded to the Apply tab: prefills its company + job selects with the
   *  host page's current selection so applying to the current job order is
   *  one click. Absent (e.g. /new-applicants) — no prefill. */
  defaultApplyTarget?: { companyId: string; companyName: string; jobOrderId: string } | null;
  /** Recsys D17 grading opt-in. When provided (only the candidate-recommendations
   *  detail modal passes it), render the 0-3 GradeWidget in the Detail tab.
   *  Absent (e.g. /new-applicants) — no widget, behavior unchanged. */
  gradingJobOrderId?: string;
  /** Forwarded to the Apply tab: JobApplicant.source recorded when the apply
   *  creates a new application. Absent — backend default "Recruiter". */
  applySource?: string;
}

const WorkspacePdfViewer: FC<{ url: string; filename: string }> = ({ url, filename }) => {
  const handleDownload = () => downloadSignedUrlFile(url, filename);
  const handleOpenNewTab = () => window.open(url, '_blank');

  return (
    <div className="workspace-pdf-viewer">
      <div className="workspace-pdf-viewer__toolbar">
        <span className="workspace-pdf-viewer__filename" title={filename}>
          {filename}
        </span>
        <div className="workspace-pdf-viewer__divider" />
        <button
          className="workspace-pdf-viewer__toolbar-btn"
          onClick={handleDownload}
          title="Download"
        >
          ⬇ Download
        </button>
        <button
          className="workspace-pdf-viewer__toolbar-btn"
          onClick={handleOpenNewTab}
          title="Open in new tab"
        >
          ↗ Open
        </button>
      </div>
      <div className="workspace-pdf-viewer__frame-wrap">
        <iframe src={url} title={filename} allowFullScreen />
      </div>
    </div>
  );
};

const CandidateWorkspacePanel: FC<Props> = ({
  isOpen,
  toggle,
  applicant,
  onDeclined,
  setStatusOptions,
  onStatusChange,
  onProcessed,
  showDisabledDispositions,
  onApplied,
  onShortlist,
  onDecline,
  onRestore,
  dispositionStatus,
  defaultApplyTarget,
  gradingJobOrderId,
  applySource,
}) => {
  const candidate = applicant?.candidate;
  const resumes: any[] = candidate?.resume ?? [];
  const [activeResume, setActiveResume] = useState<any>(resumes[0] ?? null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [settingStatus, setSettingStatus] = useState(false);
  const [dispositioning, setDispositioning] = useState<'shortlist' | 'decline' | 'restore' | null>(null);
  // Recsys read-only mode: real JobApplicant Set-Status/Decline actions are
  // suppressed both when the caller passes real handlers AND when it passes
  // the (now-fallback) showDisabledDispositions flag — either way this is the
  // candidate-recommendations page, not /new-applicants.
  const isRecsysDispositionMode = !!(onShortlist || onDecline || onRestore || showDisabledDispositions);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [tab, setTab] = useState<WorkspaceTab>('detail');
  // Tabs are mounted lazily on first visit, then kept mounted. This keeps an
  // in-progress note alive across tab switches while ensuring the ReactQuill
  // editor first initialises while visible (Quill breaks if created hidden).
  const [visitedTabs, setVisitedTabs] = useState<Set<WorkspaceTab>>(new Set<WorkspaceTab>(['detail']));
  const [currentStatus, setCurrentStatus] = useState<string | undefined>(applicant?.JobAppStatus);
  const [contextStatus, setContextStatus] = useState<string | null>(null);
  const [prefillCat, setPrefillCat] = useState<string | null>(null);
  const isSubmitting = useRef(false);

  const [signedUrlGetter] = useMutation(GET_SIGNED_URL);
  const [updateApplicantStatus] = useMutation(UPDATE_APPLICANT_UPDATE);

  useEffect(() => {
    if (isOpen) {
      const firstResume = candidate?.resume?.[0] ?? null;
      setActiveResume(firstResume);
      setSignedUrl(null);
      setDeclining(false);
      setTab('detail');
      setVisitedTabs(new Set<WorkspaceTab>(['detail']));
      setCurrentStatus(applicant?.JobAppStatus);
      setContextStatus(null);
      setPrefillCat(null);
      isSubmitting.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate?.id]);

  // Record each tab the first time it becomes active so it stays mounted after.
  useEffect(() => {
    setVisitedTabs((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, [tab]);

  useEffect(() => {
    if (!isOpen || !activeResume) return;
    setSignedUrl(null);
    const path = activeResume.pdfVersionUrl || activeResume.originalUrl;
    if (!path) return;

    let isMounted = true;
    signedUrlGetter({ variables: { remoteFilePath: path } })
      .then((res) => {
        if (!isMounted) return;
        const url = res?.data?.getSignedUrl?.signedUrl;
        if (url) setSignedUrl(url);
      })
      .catch(() => {/* signed URL fetch failed — viewer stays blank */});

    return () => { isMounted = false; };
  }, [isOpen, activeResume?.id]);

  const handleDecline = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setDeclining(true);
    try {
      await updateApplicantStatus({
        variables: { applicantId: applicant.id, status: 'DECLINED' },
      });
      toast.success('Candidate declined');
      onDeclined?.(applicant.id);
      toggle();
    } catch {
      toast.error('Failed to decline candidate');
    } finally {
      setDeclining(false);
      isSubmitting.current = false;
    }
  };

  const handleAddToShortlist = async () => {
    if (isSubmitting.current || !onShortlist || !candidate?.id) return;
    isSubmitting.current = true;
    setDispositioning('shortlist');
    try {
      await onShortlist(candidate.id);
      toast.success('Candidate added to shortlist');
      toggle();
    } catch {
      // onShortlist (the recsys context handler) already toasts the failure;
      // don't double-toast here.
    } finally {
      setDispositioning(null);
      isSubmitting.current = false;
    }
  };

  const handleRecsysDecline = async () => {
    if (isSubmitting.current || !onDecline || !candidate?.id) return;
    isSubmitting.current = true;
    setDispositioning('decline');
    try {
      await onDecline(candidate.id);
      toast.success('Candidate declined');
      toggle();
    } catch {
      // onDecline (the recsys context handler) already toasts the failure;
      // don't double-toast here.
    } finally {
      setDispositioning(null);
      isSubmitting.current = false;
    }
  };

  const handleRecsysRestore = async () => {
    if (isSubmitting.current || !onRestore || !candidate?.id) return;
    isSubmitting.current = true;
    setDispositioning('restore');
    try {
      await onRestore(candidate.id);
      toast.success('Candidate restored to the pool');
      toggle();
    } catch {
      // onRestore (the recsys context handler) already toasts the failure;
      // don't double-toast here.
    } finally {
      setDispositioning(null);
      isSubmitting.current = false;
    }
  };

  const handleSetStatus = async (statusValue: string, statusLabel: string) => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setSettingStatus(true);
    setStatusDropdownOpen(false);
    try {
      await updateApplicantStatus({
        variables: { applicantId: applicant.id, status: statusValue },
      });

      const noteCategory = STATUS_TO_CATEGORY[statusValue];
      if (noteCategory) {
        // Called / Emailed: keep the modal open and drop into Notes so the
        // recruiter can log what happened (which also grants ownership).
        setCurrentStatus(statusValue);
        setContextStatus(statusLabel);
        setPrefillCat(noteCategory);
        setTab('notes');
        toast.success(`Status set to ${statusLabel}`);
        onProcessed?.(applicant.id);
      } else {
        // Keep / Contact Later: set status and close out of the queue.
        toast.success(`Status set to ${statusLabel}`);
        onStatusChange?.(applicant.id, statusValue);
        toggle();
      }
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSettingStatus(false);
      isSubmitting.current = false;
    }
  };

  const primaryPhone = candidate?.phones?.find((p: any) => p.isPrimary)?.phoneNumber;
  const primaryEmail = candidate?.emails?.find((e: any) => e.isPrimary)?.email;

  // recruiterOwner is the canonical owner field (resolved server-side to the
  // active-ownership recruiter). Fall back to candidateStats if present.
  const candidateStats: any = Array.isArray(candidate?.candidateStats)
    ? candidate?.candidateStats?.[0]
    : candidate?.candidateStats;
  const ownerName =
    candidate?.recruiterOwner?.fullName ||
    candidateStats?.recruitingOwner?.fullName ||
    '--';

  const statusLabel = currentStatus
    ? currentStatus.replace(/_/g, ' ')
    : '--';

  const isAdvancedStatus = ADVANCED_STATUSES.includes(currentStatus ?? '');

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="candidate-workspace-modal"
    >
      <div className="candidate-workspace">
        {/* Header */}
        <div className="candidate-workspace__header">
          <Button color="link" className="p-0 text-dark flex-shrink-0" onClick={toggle}>
            ‹ Back
          </Button>

          <div className="candidate-workspace__header-identity">
            <div className="candidate-workspace__header-name-row">
              {candidate?.id ? (
                <span
                  className="anchorColorText route-link candidate-workspace__header-name"
                  onClick={() => window.open(`${VIEW_CANDIDATE_ROUTE}/${candidate.id}`, '_blank')}
                >
                  {candidate?.fullName || '--'}
                </span>
              ) : (
                <span className="candidate-workspace__header-name">
                  {candidate?.fullName || '--'}
                </span>
              )}
              <span className="badge badge-secondary" style={{ whiteSpace: 'nowrap' }}>{statusLabel}</span>
            </div>
            <div className="candidate-workspace__header-subline text-muted">
              {[candidate?.jobTitle, applicant?.createdAt ? `Applied ${formatDateUS(applicant.createdAt)}` : null, candidate?.source]
                .filter(Boolean).join(' · ')}
            </div>
          </div>

          <div className="candidate-workspace__header-actions">
            {(onShortlist || onDecline || onRestore) && (
              <>
                {/* Declined candidates get ONLY Restore — they are already out
                    of the pool; the way back is restoring, not re-dispositioning. */}
                {dispositionStatus === 'DECLINED' ? (
                  onRestore && (
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      disabled={dispositioning !== null}
                      onClick={handleRecsysRestore}
                    >
                      {dispositioning === 'restore' ? 'Restoring…' : 'Restore'}
                    </Button>
                  )
                ) : (
                  <>
                    {/* Hide "Add to Shortlist" when already shortlisted. */}
                    {onShortlist && dispositionStatus !== 'SHORTLISTED' && (
                      <Button
                        color="success"
                        outline
                        size="sm"
                        disabled={dispositioning !== null}
                        onClick={handleAddToShortlist}
                      >
                        {dispositioning === 'shortlist' ? 'Adding…' : 'Add to Shortlist'}
                      </Button>
                    )}
                    {onDecline && (
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        disabled={dispositioning !== null}
                        onClick={handleRecsysDecline}
                      >
                        {dispositioning === 'decline' ? 'Declining…' : 'Decline'}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
            {!onShortlist && !onDecline && !onRestore && showDisabledDispositions && (
              <>
                <Button color="success" outline size="sm" disabled title="Shortlist — coming soon">
                  Add to Shortlist
                </Button>
                <Button color="danger" outline size="sm" disabled title="Decline — coming soon">
                  Decline
                </Button>
              </>
            )}
            {!isRecsysDispositionMode && !isAdvancedStatus && setStatusOptions && setStatusOptions.length > 0 && (
              <Dropdown isOpen={statusDropdownOpen} toggle={() => setStatusDropdownOpen(!statusDropdownOpen)} size="sm">
                <DropdownToggle caret color="warning" outline disabled={settingStatus}>
                  {settingStatus ? 'Saving…' : 'Set Status'}
                </DropdownToggle>
                <DropdownMenu right>
                  {setStatusOptions.map(({ label, value }) => (
                    <DropdownItem key={value} onClick={() => handleSetStatus(value, label)}>
                      {label}
                      {STATUS_TO_CATEGORY[value] ? <span className="text-muted ml-2" style={{ fontSize: 11 }}>+ note</span> : null}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            )}
            {!isRecsysDispositionMode && !DECLINE_HIDDEN_STATUSES.includes(currentStatus ?? '') && (
              <Button color="danger" outline size="sm" onClick={handleDecline} disabled={declining}>
                {declining ? 'Declining…' : 'Decline'}
              </Button>
            )}
            <Button color="link" className="p-0 text-muted flex-shrink-0" onClick={toggle} title="Close">
              ✕
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="candidate-workspace__body">
          {/* Left: PDF viewer */}
          <div className="candidate-workspace__resume-col">
            {resumes.length === 0 ? (
              <div className="candidate-workspace__resume-empty">
                <NoDataFound text="No Resume Found!" />
              </div>
            ) : signedUrl ? (
              <WorkspacePdfViewer
                url={signedUrl}
                filename={activeResume?.resumeFileName || 'Resume'}
              />
            ) : (
              <div className="candidate-workspace__resume-empty">
                <span className="text-muted">Loading resume…</span>
              </div>
            )}
          </div>

          {/* Right: Detail card */}
          <div className="candidate-workspace__detail-col">
              <div className="candidate-workspace__detail-inner">
                <div className="candidate-workspace__detail-header">
                  <div className="candidate-workspace__tabs" role="tablist">
                    {([
                      ['detail', 'Detail'],
                      ['notes', 'Notes'],
                      ['apply', 'Apply'],
                    ] as Array<[WorkspaceTab, string]>).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        id={`ws-tab-${key}`}
                        aria-controls={`ws-panel-${key}`}
                        aria-selected={tab === key}
                        className={`candidate-workspace__tab${tab === key ? ' active' : ''}`}
                        onClick={() => setTab(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  role="tabpanel"
                  id="ws-panel-detail"
                  aria-labelledby="ws-tab-detail"
                  style={{ display: tab === 'detail' ? 'block' : 'none' }}
                >
                {gradingJobOrderId && candidate?.id && (
                  <div className="candidate-workspace__grade">
                    <GradeWidget key={candidate.id} jobOrderId={gradingJobOrderId} candidateId={candidate.id} />
                  </div>
                )}
                <Row className="mb-2">
                  <Col xs={5}><h6 className="text-14 text-blackele">Name</h6></Col>
                  <Col xs={7}>
                    <h5 className="font-weight-normal mb-0 text-blackten">
                      {candidate?.id ? (
                        <span
                          className="anchorColorText route-link"
                          onClick={() => window.open(`${VIEW_CANDIDATE_ROUTE}/${candidate.id}`, '_blank')}
                        >
                          {candidate?.fullName || '--'}
                        </span>
                      ) : (candidate?.fullName || '--')}
                    </h5>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5}><h6 className="text-14 text-blackele">Current Job Title</h6></Col>
                  <Col xs={7}><h5 className="font-weight-normal mb-0 text-blackten">{candidate?.jobTitle || '--'}</h5></Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5}><h6 className="text-14 text-blackele">Current Company</h6></Col>
                  <Col xs={7}><h5 className="font-weight-normal mb-0 text-blackten">{candidate?.companyName || '--'}</h5></Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5}><h6 className="text-14 text-blackele">Primary Phone</h6></Col>
                  <Col xs={7}><h5 className="font-weight-normal mb-0 text-blackten">{primaryPhone || '--'}</h5></Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5}><h6 className="text-14 text-blackele">Primary Email</h6></Col>
                  <Col xs={7}><h5 className="font-weight-normal mb-0 text-blackten">{primaryEmail || '--'}</h5></Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5}><h6 className="text-14 text-blackele">Address</h6></Col>
                  <Col xs={7}>
                    <h5 className="font-weight-normal mb-0 text-blackten">
                      {countryWiseDetailedlocation(
                        candidate?.countryCode,
                        candidate?.city,
                        candidate?.stateCode,
                        candidate?.streetAddress,
                        candidate?.postalCode,
                        candidate?.candidateAddressOne,
                        candidate?.candidateAddressTwo
                      )}
                    </h5>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5}><h6 className="text-14 text-blackele">Candidate Owner</h6></Col>
                  <Col xs={7}>
                    <h5 className="font-weight-normal mb-0 text-blackten">
                      {ownerName}
                    </h5>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col xs={5}><h6 className="text-14 text-blackele">LinkedIn Profile</h6></Col>
                  <Col xs={7}>
                    <h5 className="font-weight-normal mb-0 text-blackten">
                      {candidate?.linkedInUrl ? (
                        <a href={candidate.linkedInUrl} rel="noreferrer" target="_blank">
                          <img src={LINKEDIN_ICON} alt="LinkedIn" width="21px" />
                        </a>
                      ) : '--'}
                    </h5>
                  </Col>
                </Row>

                {resumes.length > 0 && (
                  <>
                    <div className="pb-2 pt-2">
                      <strong>Resume</strong>
                    </div>
                    {resumes.map((resume) => (
                      <div
                        key={resume.id}
                        className={`candidate-drawer-resume-grid${activeResume?.id === resume.id ? ' active' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveResume(resume)}
                      >
                        <div className="resume-details">
                          <h6>
                            <span
                              className={activeResume?.id === resume.id ? 'color-default font-weight-bold' : ''}
                            >
                              {resume.resumeFileName}
                            </span>
                          </h6>
                          <h5 className="m-0">
                            Uploaded {resume?.createdAt ? formatDateUS(resume.createdAt) : '--'}
                          </h5>
                        </div>
                        <div className="flex-none">
                          <Button
                            className="border-0 btn mx-3 bg-success px-2 btn-sm text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadSignedUrlFile(resume.originalUrl, resume.resumeFileName);
                            }}
                          >
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                </div>

                {/* Mounted on first visit (so ReactQuill initialises while
                    visible), then kept mounted so an in-progress note/selection
                    survives later tab switches; only visibility toggles. */}
                {visitedTabs.has('notes') && (
                  <div
                    role="tabpanel"
                    id="ws-panel-notes"
                    aria-labelledby="ws-tab-notes"
                    style={{ display: tab === 'notes' ? 'block' : 'none' }}
                  >
                    <NotesTab
                      key={candidate?.id}
                      candidateId={candidate?.id}
                      contextStatus={contextStatus}
                      prefillCategory={prefillCat}
                      onClearContext={() => setContextStatus(null)}
                    />
                  </div>
                )}

                {visitedTabs.has('apply') && (
                  <div
                    role="tabpanel"
                    id="ws-panel-apply"
                    aria-labelledby="ws-tab-apply"
                    style={{ display: tab === 'apply' ? 'block' : 'none' }}
                  >
                    <ApplyTab
                      key={candidate?.id}
                      candidateId={candidate?.id}
                      currentApplicantId={applicant?.id}
                      onApplied={onApplied}
                      defaultApplyTarget={defaultApplyTarget}
                      applySource={applySource}
                    />
                  </div>
                )}
              </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CandidateWorkspacePanel;
